import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TableSimple, type TableProps } from '@/components/TableSimple';
import { SavedViews } from '@/components/SavedViews';
import { ListSettings } from '@/components/ListSettings';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { ListPanel } from '@/components/ListPanel';
import { listViewSchema, type ListView } from '@/components/listPreferences';
import { useLocalList } from '@/hooks/useLocalList';
import { useStoredPreference } from '@/hooks/useStoredPreference';
import { Select } from '@/components/Select';
import { DatePicker } from '@/components/DatePicker';

export interface TableViewOptions {
    viewKey?: string;
    viewStorageKey?: string;
    viewFilters?: Record<string, string>;
    viewDefaultFilters?: Record<string, string>;
    onRestoreView?: (filters: Record<string, string>, search: string) => void;
    onClearViewFilters?: () => void;
}

const fieldValue = (row: object, key: string): unknown => key.split('.').reduce<unknown>((entry, part) => entry && typeof entry === 'object' ? (entry as Record<string, unknown>)[part] : undefined, row);
const searchableValue = (value: unknown): string => {
    if(value == null){ return ''; }
    if(Array.isArray(value)){ return value.map(searchableValue).join(' '); }
    if(typeof value === 'object'){ return Object.values(value).map(searchableValue).join(' '); }
    return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
};

export const ManagedTable = <T extends object,>({ viewKey, viewStorageKey, viewFilters = {}, viewDefaultFilters = {}, onRestoreView, onClearViewFilters, ...props }: TableProps<T> & TableViewOptions & { viewKey: string }) => {
    const { t } = useTranslation('global');
    const [open, setOpen] = useState(false);
    const [storedSearch, storeSearch] = useStoredPreference(`${viewKey}:search`, z.string().max(500), '', true);
    const [columnFilters, setColumnFilters] = useStoredPreference(`${viewKey}:filters`, z.record(z.string(), z.string()), {}, true);
    const search = props.searchValue ?? storedSearch;
    const onSearch = props.onSearchChange ?? storeSearch;
    const list = useLocalList({ key: viewKey, search, onSearch, initialSort: { key: props.defaultSortKey ?? props.columns.find(column => column.sortable !== false)?.key ?? '', order: props.defaultSortOrder ?? 'asc' },
        filters: { ...viewFilters, ...columnFilters }, defaultFilters: viewDefaultFilters });
    const restore = (saved: ListView): void => {
        list.apply(saved);
        setColumnFilters(Object.fromEntries(Object.entries(saved.filters).filter(([key]) => key.startsWith('column:'))));
        onRestoreView?.(Object.fromEntries(Object.entries(saved.filters).filter(([key]) => !key.startsWith('column:'))), saved.search);
    };
    const term = searchableValue(search.trim());
    const searchKeys = props.searchKeys?.length ? props.searchKeys : props.columns.map(column => column.searchKey ?? column.key);
    const filtered = props.data.filter(row => (Boolean(props.onSearchChange) || !term || searchKeys.some(key => searchableValue(fieldValue(row, key)).includes(term))) &&
        Object.entries(columnFilters).every(([key, value]) => {
            const column = props.columns.find(entry => entry.key === key.slice(7));
            const rowValue = searchableValue(fieldValue(row, column?.searchKey ?? key.slice(7)));
            if(column?.type === 'select' || column?.type === 'boolean'){
                return value.split(',').filter(Boolean).some(option => rowValue === searchableValue(option));
            }
            return rowValue.includes(searchableValue(value.trim()));
        }));
    const updateFilter = (key: string, value: string): void => setColumnFilters(previous => {
        const next = { ...previous, [`column:${key}`]: value };
        if(!value){ delete next[`column:${key}`]; }
        return next;
    });
    const clearFilters = (): void => { setColumnFilters({}); onClearViewFilters?.(); };
    const customFilters = props.filterElement || props.filters;
    const automaticFilters = props.columns.map(column => {
        if(props.search !== false && (column.type === 'text' || column.type === 'number' || !column.type)){
            return null;
        }

        const values = Array.from(new Set(props.data.map(row => fieldValue(row, column.key)).filter(value => value !== null && value !== undefined && value !== '')));
        const options = column.filterOptions ?? values.map(value => ({ value: String(value), label: String(value) }));
        const value = columnFilters[`column:${column.key}`] ?? '';

        if(column.type === 'select' || column.type === 'boolean'){
            const selectOptions = column.type === 'boolean'
                ? [{ value: 'true', label: t('common.yes') }, { value: 'false', label: t('common.no') }]
                : options;
            const selected = value ? value.split(',').filter(Boolean) : [];
            return <Select key={column.key} label={column.label} full multiple value={selected} options={selectOptions} onChange={(next: string[]) => updateFilter(column.key, next.join(','))} />;
        }

        if(column.type === 'date' || column.type === 'datetime'){
            return <DatePicker key={column.key} label={column.label} display="dropdown" value={value || null} onChange={(next) => updateFilter(column.key, next || '')} clearable full />;
        }

        if(props.search === false && (column.type === 'text' || column.type === 'number' || !column.type)){
            return <Input key={column.key} label={column.label} type={column.type === 'number' ? 'number' : 'text'} value={value} onChange={event => updateFilter(column.key, event.target.value)} />;
        }

        return null;
    }).filter(Boolean);
    const hasFilterControls = Boolean(customFilters) || automaticFilters.length > 0;
    const hasFilters = Object.values(columnFilters).some(Boolean) || Object.values(viewFilters).some(value => !['', 'all', '[]'].includes(value));
    return <>
        <TableSimple<T> {...props} appearance="workspace" searchable={props.searchable ?? true} data={filtered}
            columns={props.columns.filter(column => !list.preferences.hidden.includes(column.key))} density={list.preferences.density}
            searchValue={search} onSearchChange={onSearch} sort={list.sort} onSortChange={list.setSort}
            pagination={props.paginated === false ? undefined : list.pagination(filtered.length)} manualPagination={false}
            filterOpen={open}
            onFilterToggle={hasFilterControls ? () => setOpen(previous => !previous) : undefined}
            filterLabel={t('providers.ui.filters')}
            filterElement={<div className="flex max-w-full flex-wrap items-center gap-2">
                {(props.views !== false || props.config !== false) && (
                    <SavedViews
                        views={props.views !== false}
                        config={props.config !== false}
                        storageKey={viewStorageKey ?? `${viewKey}:views`}
                        schema={listViewSchema}
                        value={list.view}
                        defaultValue={list.defaultView}
                        onApply={restore}
                        configuration={
                            <ListSettings
                                inline
                                columns={props.columns}
                                value={list.preferences}
                                onChange={list.setPreferences}
                                sort={list.sort}
                                onSortChange={list.setSort}
                            />
                        }
                    />
                )}
            </div>}
            filterPanel={<ListPanel isOpen={open} onClose={() => setOpen(false)} title={t('providers.ui.filters')}
                footer={<Button variant="ghost" size="sm" onClick={clearFilters}>{t('workspace.list.clear')}</Button>}>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {customFilters || automaticFilters}
                </div>
            </ListPanel>}
            filterFooter={<div className="flex flex-wrap gap-2">
                {props.filterFooter}
                {Object.entries(columnFilters).filter(([, value]) => value).map(([key]) => <Button key={key} size="xs" variant="outline" icon="x" onClick={() => updateFilter(key.slice(7), '')}>{props.columns.find(column => column.key === key.slice(7))?.label}</Button>)}
                {hasFilters && !open && <Button size="xs" variant="ghost" onClick={clearFilters}>{t('workspace.list.clear')}</Button>}
            </div>} />
    </>;
};
